"use client";

import type { AssistantContextProjection } from "@/lib/api/projection-types";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

interface Props {
  context?: AssistantContextProjection | null;
  status: "idle" | "loading" | "ready" | "error";
  screenContext?: string;
}

// Exported so tests and other components can import the canonical
// surface → label map instead of duplicating it. The Decide tab has
// four real subtabs (goals/library/decisions/balance); collapsing
// them all into "Review" was the root of the UAT 2026-05-06
// "Goals subtab → Grounded in Review" bug.
export function screenLabel(screenContext?: string): string {
  const labels: Record<string, string> = {
    home: "Today",
    present: "Today",
    today: "Today",
    schedule: "Plan",
    plan: "Plan",
    decide: "Review",
    decisions: "Decisions",
    library: "Library",
    balance: "Balance",
    life: "Reflect",
    reflect: "Reflect",
    goals: "Goals",
    queue: "Review",
    settings: "Settings",
  };
  return labels[screenContext || "home"] || "your current workspace";
}

function toneLabel(tonePreference?: string | null): string | null {
  if (!tonePreference) return null;
  return tonePreference.replace(/_/g, " ");
}

function buildSeeingSummary(context: AssistantContextProjection): string {
  if (context.today_restart) {
    return `Your clearest restart is ${sanitizeSurfaceTitle(context.today_restart.title)}.`;
  }
  if (context.recovery_target) {
    return `The main recovery target is ${sanitizeSurfaceTitle(context.recovery_target.title)}.`;
  }
  if (context.active_goals.length > 0) {
    return `I’m grounding suggestions in ${sanitizeSurfaceTitle(context.active_goals[0].title)}.`;
  }
  return "I’ll stay grounded in the current projected state instead of guessing.";
}

export default function ChatContextHeader({ context, status, screenContext }: Props) {
  if (!context && status === "error") {
    return null;
  }

  if (!context) {
    return (
      <section
        className="mb-4 rounded-2xl border border-border/80 bg-sage-pale/35 px-4 py-3"
        data-testid="chat-context-header"
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          What I&apos;m Seeing
        </p>
        <p className="mt-1 text-sm text-text-light">
          Pulling in the latest {screenLabel(screenContext)} context before I suggest a next move.
        </p>
      </section>
    );
  }

  const queuePreview = context.queue_peek
    .slice(0, 2)
    .map((item) => sanitizeSurfaceTitle(item.title))
    .join(" · ");
  const driftingGoals = context.active_goals
    .filter((goal) => goal.status === "drifting")
    .map((goal) => sanitizeSurfaceTitle(goal.title))
    .slice(0, 2);
  const steadyGoals = context.active_goals
    .filter((goal) => goal.status === "steady")
    .map((goal) => sanitizeSurfaceTitle(goal.title))
    .slice(0, 2);
  const tone = toneLabel(context.tone_preference);

  return (
    <section
      className="mb-4 rounded-2xl border border-border/80 bg-sage-pale/35 px-4 py-3 shadow-sm"
      data-testid="chat-context-header"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
            What I&apos;m Seeing
          </p>
          <h3 className="mt-1 text-sm font-semibold text-text">
            Grounded in {screenLabel(screenContext)}
          </h3>
          <p className="mt-1 text-sm text-text-light">{buildSeeingSummary(context)}</p>
        </div>
        {tone && (
          <span className="rounded-full border border-border/80 bg-card/80 px-2.5 py-1 text-[10px] font-medium text-muted">
            {tone}
          </span>
        )}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {context.today_restart && (
          <div className="rounded-xl bg-card/80 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Restart</p>
            <p className="mt-1 text-sm font-medium text-text">{sanitizeSurfaceTitle(context.today_restart.title)}</p>
          </div>
        )}
        {context.recovery_target && (
          <div className="rounded-xl bg-card/80 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Needs Recovery</p>
            <p className="mt-1 text-sm font-medium text-text">{sanitizeSurfaceTitle(context.recovery_target.title)}</p>
          </div>
        )}
        {driftingGoals.length > 0 && (
          <div className="rounded-xl bg-card/80 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Watchouts</p>
            <p className="mt-1 text-sm text-text-light">{driftingGoals.join(" · ")}</p>
          </div>
        )}
        {steadyGoals.length > 0 && (
          <div className="rounded-xl bg-card/80 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Holding Steady</p>
            <p className="mt-1 text-sm text-text-light">{steadyGoals.join(" · ")}</p>
          </div>
        )}
        {queuePreview && (
          <div className="rounded-xl bg-card/80 px-3 py-2 sm:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Queue Peek</p>
            <p className="mt-1 text-sm text-text-light">{queuePreview}</p>
          </div>
        )}
      </div>

      <p className="mt-3 text-[11px] text-muted">
        If this context looks off, use <span className="font-medium text-text-light">Correct me</span> on the reply below.
      </p>
    </section>
  );
}
